import { Stack, Text } from "@certn/cascadia";
import { Card, Link } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { formatMonitoringDate } from "../monitoringFormat";
import { isNoteworthyRunResult } from "../monitoringEnums";
import { getVisibleMonitoringRunChanges } from "../monitoringRunChangeText";
import type { EnrollmentDetailCheck } from "../types";
import { MonitoringAlertNotificationsTable } from "./MonitoringAlertNotificationsTable";
import { MonitoringChangeDetailsTable } from "./MonitoringChangeDetailsTable";
import {
  enrollmentToRunDetail,
  MonitoringRunResultTagWithTooltip,
} from "./MonitoringRunResultTagWithTooltip";
import { MonitorStatusTag } from "./monitoringTags";

function StatusPill({
  monitorStatus,
  enrollment,
}: {
  monitorStatus: string;
  enrollment: EnrollmentDetailCheck;
}) {
  if (monitorStatus !== "ACTIVE") {
    return <MonitorStatusTag status={monitorStatus} />;
  }

  const lastRunResult = enrollment.last_run_result;
  if (lastRunResult && isNoteworthyRunResult(lastRunResult)) {
    return (
      <MonitoringRunResultTagWithTooltip
        result={lastRunResult}
        details={[enrollmentToRunDetail(enrollment)]}
      />
    );
  }

  return <MonitorStatusTag status="ACTIVE" />;
}

export interface CheckMonitoredCardProps {
  enrollment: EnrollmentDetailCheck;
  renderActions?: (enrollment: EnrollmentDetailCheck) => ReactNode;
  renderCaseLink?: (shortId: string) => ReactNode;
  renderMonitorCaseLink?: (shortId: string) => ReactNode;
  renderReferenceCaseLink?: (shortId: string) => ReactNode;
  renderDemoResponseEditor?: (enrollmentId: string) => ReactNode;
}

const cardBodyStyle = css({
  display: "flex",
  flexDirection: "column",
});

const cardMainStyle = css({
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-2)",
});

const showMoreStyle = css({
  marginTop: "var(--space-6)",
});

const headingRowStyle = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "var(--space-3)",
  minHeight: "40px",
});

const titleGroupStyle = css({
  display: "flex",
  alignItems: "center",
  gap: "var(--space-2)",
});

const metadataGridStyle = css({
  display: "grid",
  gridTemplateColumns: "minmax(0, 3fr) minmax(0, 9fr)",
  columnGap: "var(--space-4)",
  rowGap: "var(--space-3)",
  justifyContent: "start",
  width: "100%",
});

const metadataRowStyle = css({
  display: "contents",
});

const expandedBodyStyle = css({
  paddingTop: "var(--space-4)",
});

const metadataSectionStyle = css({
  width: "100%",
});

const borderedTableContainerStyle = css({
  border: "1px solid var(--border-base)",
  borderRadius: "var(--border-radius-3)",
  width: "100%",
  overflow: "hidden",
});

function MetadataRow({
  label,
  value,
  testID,
  labelStyle = "global/heading-small",
}: {
  label: string;
  value: ReactNode;
  testID?: string;
  labelStyle?: "global/heading-small" | "form/body-secondary";
}) {
  const isPlainValue = typeof value === "string" || typeof value === "number";

  return (
    <div className={metadataRowStyle} data-testid={testID}>
      <Text style={labelStyle}>{label}</Text>
      {isPlainValue ? <Text style="form/body-secondary">{value}</Text> : value}
    </div>
  );
}

function DefaultCaseLink({ shortId }: { shortId: string }) {
  return (
    <Link href={`/case-details/${shortId}`} testID="monitoring-case-link">
      {shortId}
    </Link>
  );
}

export function CheckMonitoredCard({
  enrollment,
  renderActions,
  renderCaseLink,
  renderMonitorCaseLink,
  renderReferenceCaseLink,
  renderDemoResponseEditor,
}: CheckMonitoredCardProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const renderMonitorLink =
    renderMonitorCaseLink ??
    renderCaseLink ??
    ((shortId) => <DefaultCaseLink shortId={shortId} />);
  const renderReferenceLink =
    renderReferenceCaseLink ??
    renderCaseLink ??
    ((shortId) => <DefaultCaseLink shortId={shortId} />);

  const lastRunLine = enrollment.last_run_date
    ? t("Last ran on {{date}}", {
        date: formatMonitoringDate(enrollment.last_run_date),
      })
    : t("No runs yet");

  const hasDetectedChanges =
    getVisibleMonitoringRunChanges(enrollment.last_run_changes).length > 0;

  const changesLabel = enrollment.last_run_date
    ? hasDetectedChanges
      ? t("Change detected")
      : t("No change")
    : null;

  const visibleLastRunChanges = getVisibleMonitoringRunChanges(
    enrollment.last_run_changes
  );

  const subtitleLine = changesLabel
    ? `${changesLabel} • ${lastRunLine}`
    : lastRunLine;

  const blockedReason =
    enrollment.monitor_status === "ENDED" && !enrollment.can_renew
      ? enrollment.renew_blocked_reason
      : null;

  const actions = renderActions?.(enrollment);
  const demoEditor = renderDemoResponseEditor?.(enrollment.id);

  return (
    <Card type="large" padding="space-4" testID={`check-card-${enrollment.id}`}>
      <div className={cardBodyStyle}>
        <div className={cardMainStyle}>
          <div className={headingRowStyle}>
            <div className={titleGroupStyle}>
              <Text style="global/heading-large">{enrollment.check_label}</Text>
              <StatusPill
                monitorStatus={enrollment.monitor_status}
                enrollment={enrollment}
              />
            </div>
            {actions ? (
              <Stack orientation="horizontal" align="center" gap="space-3">
                {actions}
              </Stack>
            ) : null}
          </div>

          {blockedReason ? (
            <div data-testid="check-renew-blocked">
              <Text
                style="form/body-secondary"
                color="text-accent-orange-strong"
              >
                {blockedReason}
              </Text>
            </div>
          ) : (
            <Text style="form/body-secondary" color="text-weak">
              {subtitleLine}
            </Text>
          )}

          {isExpanded && (
            <Stack gap="space-5" className={expandedBodyStyle}>
              <div className={metadataSectionStyle}>
                <div className={metadataGridStyle}>
                  <MetadataRow
                    label={t("Next run date")}
                    value={
                      formatMonitoringDate(enrollment.next_run_date) || t("—")
                    }
                    testID="check-next-run-date"
                  />
                  <MetadataRow
                    label={t("Run frequency")}
                    value={
                      enrollment.run_frequency_days
                        ? t("Every {{count}} days", {
                            count: enrollment.run_frequency_days,
                          })
                        : t("—")
                    }
                  />
                  <MetadataRow
                    label={t("Total runs completed")}
                    value={enrollment.total_runs_completed}
                  />
                  <MetadataRow
                    label={t("Monitor end date")}
                    value={
                      formatMonitoringDate(enrollment.monitor_end_date) ||
                      t("—")
                    }
                  />
                  <MetadataRow
                    label={t("Monitor ID")}
                    value={
                      enrollment.monitor_case_short_id
                        ? renderMonitorLink(enrollment.monitor_case_short_id)
                        : t("—")
                    }
                  />
                  <MetadataRow
                    label={t("Reference case ID")}
                    value={renderReferenceLink(
                      enrollment.reference_case_short_id
                    )}
                  />
                  {visibleLastRunChanges.length > 0 && (
                    <MetadataRow
                      label={t("Change details")}
                      value={
                        <div className={borderedTableContainerStyle}>
                          <MonitoringChangeDetailsTable
                            changes={visibleLastRunChanges}
                          />
                        </div>
                      }
                    />
                  )}
                  {enrollment.alert_notifications.length > 0 && (
                    <MetadataRow
                      label={t("Alert notification")}
                      value={
                        <div className={borderedTableContainerStyle}>
                          <MonitoringAlertNotificationsTable
                            alerts={enrollment.alert_notifications}
                          />
                        </div>
                      }
                    />
                  )}
                </div>
              </div>
              {demoEditor}
            </Stack>
          )}
        </div>
        <div className={showMoreStyle}>
          <Link
            linkStyle="monochrome"
            textStyle="form/body-secondary"
            onPress={() => setIsExpanded((prev) => !prev)}
            testID="check-show-more-toggle"
          >
            {isExpanded ? t("Show less") : t("Show more")}
          </Link>
        </div>
      </div>
    </Card>
  );
}
